import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getAdminSaleOrders, type AdminSaleOrderWithPhase } from '../lib/api'

const STATUS_LABEL: Record<string, string> = {
  pending: 'Menunggu',
  confirmed: 'Dikonfirmasi',
  rejected: 'Ditolak',
}
const STATUS_CLASS: Record<string, string> = {
  pending: 'bg-yellow-900 text-yellow-400',
  confirmed: 'bg-green-900 text-green-400',
  rejected: 'bg-red-900 text-red-400',
}

type OrderTab = 'pending' | 'confirmed' | 'rejected' | 'all'

const TAB_LABELS: { key: OrderTab; label: string }[] = [
  { key: 'pending', label: 'Menunggu' },
  { key: 'confirmed', label: 'Dikonfirmasi' },
  { key: 'rejected', label: 'Ditolak' },
  { key: 'all', label: 'Semua' },
]

export default function SaleEventOrdersPage() {
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  const navigate = useNavigate()

  const [orders, setOrders] = useState<AdminSaleOrderWithPhase[]>([])
  const [loading, setLoading] = useState(true)
  const [orderTab, setOrderTab] = useState<OrderTab>('pending')

  useEffect(() => {
    if (!id || !token) return
    setLoading(true)
    getAdminSaleOrders(token, id)
      .then(setOrders)
      .finally(() => setLoading(false))
  }, [id, token])

  const pendingCount = orders.filter(o => o.status === 'pending').length
  const filteredOrders = orderTab === 'all' ? orders : orders.filter(o => o.status === orderTab)

  return (
    <div className="p-6">
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">

        {/* Tab bar */}
        <div className="flex items-center border-b border-zinc-800 px-5">
          {TAB_LABELS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setOrderTab(key)}
              className={`relative flex items-center gap-1.5 px-1 py-3.5 mr-5 text-xs font-medium transition-colors border-b-2 ${
                orderTab === key
                  ? 'text-white border-yellow-500'
                  : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`}
            >
              {label}
              {key === 'pending' && pendingCount > 0 && (
                <span className="bg-yellow-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Orders table */}
        {loading ? (
          <div className="p-8 text-center">
            <p className="text-zinc-500 text-sm">Loading…</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-zinc-500 text-sm">
              {orderTab === 'pending' && 'Tidak ada order yang menunggu konfirmasi.'}
              {orderTab === 'confirmed' && 'Belum ada order yang dikonfirmasi.'}
              {orderTab === 'rejected' && 'Belum ada order yang ditolak.'}
              {orderTab === 'all' && 'Belum ada order masuk.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-zinc-300">
              <thead className="bg-zinc-800/60 text-[10px] uppercase text-zinc-500">
                <tr>
                  <th className="px-5 py-3 text-left">Kode Order</th>
                  <th className="px-5 py-3 text-left">Nama Pembeli</th>
                  <th className="px-5 py-3 text-left">Phase</th>
                  <th className="px-5 py-3 text-left">Qty</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Tanggal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filteredOrders.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    onClick={() => navigate(`/sales/orders/${o.id}`)}
                  >
                    <td className="px-5 py-3 font-mono text-xs text-zinc-400">{o.order_code}</td>
                    <td className="px-5 py-3 font-medium">{o.buyer_name}</td>
                    <td className="px-5 py-3 text-zinc-500 text-xs">{o.sale_event_phases?.name ?? '—'}</td>
                    <td className="px-5 py-3">{o.quantity}</td>
                    <td className="px-5 py-3">
                      <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_CLASS[o.status]}`}>
                        {STATUS_LABEL[o.status]}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-zinc-500">
                      {new Date(o.created_at).toLocaleDateString('id-ID')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
